import type { AIResponseDTO } from "./ai-responses"
import type { ChatMessageDTO } from "./chat"
import type { NotificationPreferencesUpdatedPayload } from "./notification-preferences"
import type { NotificationDTO } from "./notifications"
import type { TicketStatus } from "./tickets"

export interface ChatThreadAcknowledgedPayload {
	chatId: string
	unreadMessageCount: number
}

export interface MembershipChangedPayload {
	propertyId: string
}

/**
 * A property's editable content changed — name and/or description (incl. the
 * AI-generated summaries) (ZPL-186). Pushed on the property channel after every
 * write path so the web re-fetches it on both the list and the detail view.
 */
export interface PropertyUpdatedPayload {
	propertyId: string
}

export interface InspirationChangedPayload {
	propertyId: string
	projectId: string
}

export interface VisualizationChangedPayload {
	propertyId: string
	projectId: string
}

// Emitted on the property channel whenever a property's topics change
// (created/updated/deleted); the web refetches the topic list + the board.
interface TopicChangedPayload {
	propertyId: string
}

// Emitted on the property channel when a project's editable fields change
// (ZPL-259) — name/description/cost; the web refetches the project detail + list.
interface ProjectChangedPayload {
	propertyId: string
	projectId: string
}

// A document appeared (upload confirm), changed (title/aiReadable) or was
// deleted (ZPL-282). projectId is set for project-scoped documents so the
// web can invalidate that project's list alongside the property list.
interface DocumentChangedPayload {
	propertyId: string
	projectId: string | null
	documentId: string
}

// The property was (soft-)deleted (ZPL-282) — open viewers evict their
// cached queries instead of keeping a dead property live.
interface PropertyDeletedPayload {
	propertyId: string
}

// A document grant/revoke changed the recipient's access (ZPL-259). Delivered on
// the grantee's per-user channel; carries propertyId so the web invalidates that
// property's document queries.
interface DocumentAccessChangedPayload {
	propertyId: string
	documentId: string
}

/**
 * Ticket lifecycle events (ZPL-157) — fanned per-recipient on each reader's
 * `private-user-{id}` channel (NEVER the property channel; ticket visibility is
 * per-row `Task.read`). Payload carries ids for a targeted refetch.
 */
export interface TicketRealtimePayload {
	ticketId: string
	propertyId: string
}

export interface TicketStatusChangedPayload {
	ticketId: string
	propertyId: string
	status: TicketStatus
}

export interface TicketCommentAddedPayload {
	ticketId: string
	propertyId: string
	commentId: string
}

/** A ticket comment was soft-deleted — drop it from the open comment list. */
interface TicketCommentDeletedPayload {
	ticketId: string
	propertyId: string
	commentId: string
}

/** A reader lost access (assignee/scope change, delete) — purge the local row. */
export interface TicketAccessLostPayload {
	ticketId: string
}

interface JournalAppendedPayload {
	propertyId: string
	projectId: string | null
	eventId: string
	type: string
}

/**
 * A user was @-mentioned in a shared chat (ZPL-147). Pushed on the recipient's
 * app-wide per-user channel (`private-user-<userId>`) so the web can toast it
 * from anywhere. `byDisplayName` is the mentioner; `preview` is a short snippet.
 */
export interface ChatMentionedPayload {
	propertyId: string
	projectId: string | null
	chatId: string
	messageId: string
	byDisplayName: string
	preview: string
}

/**
 * A user was @-mentioned in a ticket comment (ZPL-154 extra). Pushed on the
 * recipient's per-user channel (`private-user-<userId>`) so any open client can
 * toast it. `byDisplayName` is the comment author; `preview` a short snippet.
 */
export interface TicketCommentMentionPayload {
	ticketId: string
	propertyId: string
	commentId: string
	byDisplayName: string
	preview: string
}

/**
 * A user was @-mentioned in a ticket description (ZPL-178). Same per-user
 * channel + toast as the comment mention, without `commentId` because the
 * target is the ticket itself. `byDisplayName` is the editor; `preview` a
 * description snippet.
 */
export interface TicketMentionedPayload {
	ticketId: string
	propertyId: string
	byDisplayName: string
	preview: string
}

interface BudgetEntryChangedPayload {
	propertyId: string
	/** The entry's project scope, when any — lets clients target both views. */
	projectId: string | null
}

interface MareczekThinkingPayload {
	text: string
	/** The chat thread the agent is working in — clients key the indicator by it (ZPL-152). */
	threadId: string
}

interface MareczekThinkingClearedPayload {
	threadId: string
}

/**
 * Envelope for every message pushed over a realtime channel. The `event`
 * field is the discriminator; new event types are added as union variants
 * (no wire migration since clients route by `event`).
 */
export type RealtimeMessage =
	| { event: "ai-response.created"; payload: AIResponseDTO }
	| { event: "ai-response.processed"; payload: AIResponseDTO }
	| { event: "ai-response.failed"; payload: AIResponseDTO }
	| { event: "chat-message.created"; payload: ChatMessageDTO }
	// OWNER-ONLY invalidation ping (ZPL-301): something changed in the
	// property's budget (entry created/edited/deleted/status-flipped, capture
	// confirmed, extraction filled a draft). Dispatched ONLY on the owner's
	// per-user channel — never a member-visible channel — and deliberately
	// carries NO amounts/fields: clients just refetch their budget queries.
	| { event: "budget-entry.changed"; payload: BudgetEntryChangedPayload }
	// In-place content change of an existing message — today only the agent
	// approval card's status flip (ZPL-295). Clients REPLACE the cached
	// message by id so every viewer/device sees the resolved card live.
	| { event: "chat-message.updated"; payload: ChatMessageDTO }
	| {
			event: "chat-thread.acknowledged"
			payload: ChatThreadAcknowledgedPayload
	  }
	| { event: "membership.changed"; payload: MembershipChangedPayload }
	| { event: "property.updated"; payload: PropertyUpdatedPayload }
	| { event: "journal.appended"; payload: JournalAppendedPayload }
	| { event: "inspiration.changed"; payload: InspirationChangedPayload }
	| { event: "visualization.changed"; payload: VisualizationChangedPayload }
	| { event: "chat.mentioned"; payload: ChatMentionedPayload }
	| { event: "notification.created"; payload: NotificationDTO }
	| {
			event: "notification.read"
			payload: { notificationIds: string[] }
	  }
	// Mareczek agent "thinking" indicator — pushed on the TARGET chat's channel
	// (per-user private channel for 1:1 chats, resource-wide channel for shared
	// property/project chats) between an agent turn's tool call and its
	// eventual final answer. Keyed client-side by the payload's threadId.
	| { event: "mareczek.thinking"; payload: MareczekThinkingPayload }
	| {
			event: "mareczek.thinking-cleared"
			payload: MareczekThinkingClearedPayload
	  }
	| { event: "ticket.created"; payload: TicketRealtimePayload }
	| { event: "ticket.assigned"; payload: TicketRealtimePayload }
	| { event: "ticket.updated"; payload: TicketRealtimePayload }
	| { event: "ticket.status_changed"; payload: TicketStatusChangedPayload }
	| { event: "ticket.deleted"; payload: TicketRealtimePayload }
	| { event: "ticket.comment.added"; payload: TicketCommentAddedPayload }
	| {
			event: "ticket.comment.mention"
			payload: TicketCommentMentionPayload
	  }
	| { event: "ticket.mentioned"; payload: TicketMentionedPayload }
	| { event: "ticket.access_lost"; payload: TicketAccessLostPayload }
	| { event: "ticket.comment.deleted"; payload: TicketCommentDeletedPayload }
	| { event: "topic.changed"; payload: TopicChangedPayload }
	| { event: "project.changed"; payload: ProjectChangedPayload }
	| { event: "project.access.changed"; payload: ProjectChangedPayload }
	| { event: "document.changed"; payload: DocumentChangedPayload }
	| { event: "property.deleted"; payload: PropertyDeletedPayload }
	| { event: "ticket.comment.updated"; payload: TicketCommentAddedPayload }
	| { event: "document.access.changed"; payload: DocumentAccessChangedPayload }
	| {
			event: "notification-preferences.updated"
			payload: NotificationPreferencesUpdatedPayload
	  }

export const AI_RESPONSE_EVENT_NAMES = [
	"ai-response.created",
	"ai-response.processed",
	"ai-response.failed",
] as const

export type AIResponseEventName = (typeof AI_RESPONSE_EVENT_NAMES)[number]

export const CHAT_EVENT_NAMES = [
	"budget-entry.changed",
	"chat-message.created",
	"chat-message.updated",
	"chat-thread.acknowledged",
] as const

export type ChatEventName = (typeof CHAT_EVENT_NAMES)[number]

export const MEMBERSHIP_EVENT_NAMES = ["membership.changed"] as const

export type MembershipEventName = (typeof MEMBERSHIP_EVENT_NAMES)[number]

// Emitted on the property channel after a name/description change (incl. AI
// enrichment); web re-fetches the property list + detail (ZPL-186).
export const PROPERTY_EVENT_NAMES = [
	"property.updated",
	"property.deleted",
] as const

export type PropertyEventName = (typeof PROPERTY_EVENT_NAMES)[number]

// Emitted on the property channel by the journal notification dispatcher;
// web consumes it for live journal refresh.
export const JOURNAL_EVENT_NAMES = ["journal.appended"] as const

// Emitted on the property channel whenever a project's inspirations change
// (created/confirmed/updated/deleted/enriched); the web refetches the list.
export const INSPIRATION_EVENT_NAMES = ["inspiration.changed"] as const

export type InspirationEventName = (typeof INSPIRATION_EVENT_NAMES)[number]

// Emitted on the property channel whenever a project's visualizations change
// (created/confirmed/updated/deleted/enriched); the web refetches the list.
export const VISUALIZATION_EVENT_NAMES = ["visualization.changed"] as const

export type VisualizationEventName = (typeof VISUALIZATION_EVENT_NAMES)[number]

// Emitted on the property channel whenever a property's topics change.
export const TOPIC_EVENT_NAMES = ["topic.changed"] as const

// Emitted on the property channel when a project's editable fields change.
export const PROJECT_EVENT_NAMES = ["project.changed"] as const

// Per-user channel (ZPL-282): the signed-in user gained or lost access to a
// project (project member add/remove) — refresh THEIR project views even
// when the property channel isn't mounted.
export const PROJECT_ACCESS_EVENT_NAMES = ["project.access.changed"] as const

// Property-channel document lifecycle (ZPL-282): a document appeared,
// changed or was deleted — every viewer's document lists are stale.
export const DOCUMENT_CHANGED_EVENT_NAMES = ["document.changed"] as const

// Emitted on the recipient's per-user channel when a document grant/revoke
// changes their access (ZPL-259) — NOT a property-channel event.
export const DOCUMENT_EVENT_NAMES = ["document.access.changed"] as const

// Per-user channel: a persisted notification was created for the signed-in
// user (ZPL-310) — the web bell bumps its badge and prepends the row.
export const NOTIFICATION_EVENT_NAMES = [
	"notification.created",
	"notification.read",
] as const

// Emitted on the recipient's app-wide per-user channel when they're
// @-mentioned in a shared chat; the web shows a toast.
export const MENTION_EVENT_NAMES = ["chat.mentioned"] as const

export type MentionEventName = (typeof MENTION_EVENT_NAMES)[number]

// Transient Mareczek "is working" indicator. Shares the per-user private chat
// channel with the chat-message events but routes to its own client handler.
export const MARECZEK_EVENT_NAMES = [
	"mareczek.thinking",
	"mareczek.thinking-cleared",
] as const

export type MareczekEventName = (typeof MARECZEK_EVENT_NAMES)[number]

export const TICKET_EVENT_NAMES = [
	"ticket.created",
	"ticket.assigned",
	"ticket.updated",
	"ticket.status_changed",
	"ticket.deleted",
	"ticket.comment.added",
	"ticket.comment.deleted",
	"ticket.comment.updated",
	"ticket.comment.mention",
	"ticket.mentioned",
	"ticket.access_lost",
] as const

export type TicketEventName = (typeof TICKET_EVENT_NAMES)[number]

export const NOTIFICATION_PREFERENCE_EVENT_NAMES = [
	"notification-preferences.updated",
] as const

export type NotificationPreferenceEventName =
	(typeof NOTIFICATION_PREFERENCE_EVENT_NAMES)[number]

export const REALTIME_EVENT_NAMES = [
	...AI_RESPONSE_EVENT_NAMES,
	...CHAT_EVENT_NAMES,
	...MEMBERSHIP_EVENT_NAMES,
	...PROPERTY_EVENT_NAMES,
	...JOURNAL_EVENT_NAMES,
	...INSPIRATION_EVENT_NAMES,
	...VISUALIZATION_EVENT_NAMES,
	...TOPIC_EVENT_NAMES,
	...PROJECT_EVENT_NAMES,
	...PROJECT_ACCESS_EVENT_NAMES,
	...DOCUMENT_EVENT_NAMES,
	...DOCUMENT_CHANGED_EVENT_NAMES,
	...MENTION_EVENT_NAMES,
	...NOTIFICATION_EVENT_NAMES,
	...MARECZEK_EVENT_NAMES,
	...TICKET_EVENT_NAMES,
	...NOTIFICATION_PREFERENCE_EVENT_NAMES,
] as const

export type RealtimeEventName = (typeof REALTIME_EVENT_NAMES)[number]

// Compile-time completeness guard: every `RealtimeMessage` variant's `event`
// must appear in REALTIME_EVENT_NAMES. Add a union variant above without listing
// its name and this fails to compile — exactly the gap that left the Mareczek
// "thinking" events emitted by the API but never bound on the client.
type _AllEventsListed = RealtimeMessage["event"] extends RealtimeEventName
	? true
	: [
			"unlisted realtime event(s)",
			Exclude<RealtimeMessage["event"], RealtimeEventName>,
		]
const _allEventsListed: _AllEventsListed = true
void _allEventsListed
